/**
 * What each Estreia slide shows (NEWS-28…36), from the store's facts: the
 * real number, or the slide's alternative text, or the module's sample data
 * with the "Exemplo" badge (NEWS-25), and a variation only when it rose
 * (NEWS-24, via `DeltaBadge`). Pure: the slides only render these.
 */
import { DateTime } from 'luxon';
import type {
  CampaignDto,
  TenantSalesMetricsResponseDto,
} from '@/generated/korza-api-client/types.gen';
import type { Locales, TranslationFunctions } from '@/i18n/i18n-types';
import {
  contrastRatio,
  formatContrastRatio,
  meetsAA,
  normalizeHex,
} from '@/lib/app-editor/color-contrast';
import { appShare } from '@/lib/app-share';
import { repurchaseCount } from '@/lib/sales-insights';
import { getLuxonLocale } from '@/lib/utils';
import type { I18nText } from '@/lib/whats-new/edition-types';
import {
  formatCount,
  formatDay,
  formatMoney,
} from '@/lib/whats-new/formatting';
import type {
  EstreiaFacts,
  Fact,
} from '@/components/whats-new/editions/estreia/use-estreia-facts';

/** A value still loading (skeleton, NEWS-26) or ready to show. */
export type View<T> = { state: 'loading' } | { state: 'ready'; value: T };

/** Props of `DeltaBadge`: it shows only a rise (NEWS-24). */
export interface DeltaInput {
  current: number;
  previous: number | null;
}

export interface StoreView {
  name: string;
  /** `null` → the `Store` icon. */
  iconUrl: string | null;
}

/** Shown for a number that is not available (NEWS-33 "KPI sem valor"). */
export const NO_VALUE = '—';

/**
 * "Exemplo" app revenue by day (31 days, today partial), peaking on the top
 * campaign (day 22) and the second (day 9), as in the prototype.
 */
export const SAMPLE_DAILY_REVENUE: readonly number[] = [
  723, 967, 874, 899, 1150, 1289, 1109, 1016, 968, 3197, 2065, 1174, 1496, 1129,
  936, 1003, 1125, 934, 1255, 1395, 1506, 1144, 5383, 3051, 1346, 1319, 1556,
  1409, 1240, 1177, 606,
];
export const SAMPLE_DAILY_ORDERS: readonly number[] = [
  2, 3, 3, 3, 4, 4, 4, 3, 3, 10, 7, 4, 5, 4, 3, 3, 4, 3, 4, 5, 5, 4, 17, 10, 4,
  4, 5, 5, 4, 4, 2,
];
export const SAMPLE_PEAK_DAY = 22;
export const SAMPLE_SECOND_DAY = 9;
/** "Exemplo" bars of the Analytics slide: previous × current, 7 days. */
export const SAMPLE_BAR_PAIRS: ReadonlyArray<{
  previous: number;
  current: number;
}> = [
  { previous: 40, current: 52 },
  { previous: 47, current: 60 },
  { previous: 58, current: 71 },
  { previous: 66, current: 62 },
  { previous: 75, current: 70 },
  { previous: 82, current: 78 },
  { previous: 88, current: 46 },
];

/** The CSV slide's footer (NEWS-30). */
export const CSV_SCHEDULED = 10;
export const CSV_DRAFTS = 2;

const LOADING = { state: 'loading' } as const;

/**
 * The longest campaign name the leads hold room for (NEWS-26, FX8c): the
 * long real-world-style name of the app's own fixtures (69 characters), with
 * a top campaign's realistic upper bound of R$ 999.999 in 9.999 orders.
 */
export const LONGEST_CAMPAIGN_NAME =
  'Esquenta de verão — coleção verão com frete grátis para todo o Brasil';
const LONGEST_REVENUE = 999_999;
const LONGEST_ORDERS = 9_999;
const ready = <T>(value: T): View<T> => ({ state: 'ready', value });

const storeOf = (facts: EstreiaFacts): StoreView => ({
  name: facts.store.name,
  iconUrl: facts.store.iconUrl,
});

const text =
  (value: string): I18nText =>
  () =>
    value;

/**
 * Sent campaigns of the period with attributed revenue, top first (the
 * campaigns marked on the Início's chart); hidden-data ones don't count.
 */
function topCampaigns(facts: EstreiaFacts): Fact<CampaignDto[]> {
  const fact = facts.periodCampaigns;
  if (fact.state !== 'ready') return fact;
  return {
    state: 'ready',
    value: fact.value
      .filter(
        (campaign) =>
          campaign.analytics.revenue > 0 &&
          !campaign.analytics.dataSufficiency.shouldHideData
      )
      .sort((a, b) => b.analytics.revenue - a.analytics.revenue),
  };
}

/** The top campaign, `null` without one (none sent, or the call failed). */
function topCampaign(facts: EstreiaFacts): View<CampaignDto | null> {
  const top = topCampaigns(facts);
  if (top.state === 'loading') return LOADING;
  return ready(top.state === 'ready' ? (top.value[0] ?? null) : null);
}

interface Day {
  date: string;
  revenue: number;
  orders: number;
}

/** The period's days, from its first day (sample values keep real dates). */
function periodDays(facts: EstreiaFacts, count: number): string[] {
  const start = DateTime.fromISO(facts.period.current.from);
  return Array.from(
    { length: count },
    (_, i) => start.plus({ days: i }).toISO() ?? ''
  );
}

/**
 * Daily app revenue of the period, or the sample series when the store sold
 * nothing or the call failed (NEWS-25).
 */
function revenueDays(
  facts: EstreiaFacts
): View<{ sample: boolean; days: Day[] }> {
  const home = facts.home;
  if (home.state === 'loading') return LOADING;
  if (home.state === 'ready' && home.value.current.totalPaidOrders > 0) {
    return ready({
      sample: false,
      days: home.value.current.revenueChart.points.map((point) => ({
        date: point.date,
        revenue: point.grossRevenueUsd,
        orders: point.count,
      })),
    });
  }
  const dates = periodDays(facts, SAMPLE_DAILY_REVENUE.length);
  return ready({
    sample: true,
    days: SAMPLE_DAILY_REVENUE.map((revenue, i) => ({
      date: dates[i],
      revenue,
      orders: SAMPLE_DAILY_ORDERS[i],
    })),
  });
}

/** Index of the day a campaign was sent, or -1. */
function dayIndexOf(days: Day[], campaign: CampaignDto): number {
  const launch = DateTime.fromISO(campaign.launchDateTime);
  return days.findIndex((day) =>
    DateTime.fromISO(day.date).hasSame(launch, 'day')
  );
}

/** Indexes of the `count` days with the most revenue, highest first. */
function topDays(days: Day[], count: number): number[] {
  return days
    .map((day, index) => ({ index, revenue: day.revenue }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, count)
    .map(({ index }) => index);
}

const ticketOf = (revenue: number, orders: number): number | null =>
  orders > 0 ? revenue / orders : null;

function percent(value: number, locale: Locales): string {
  return new Intl.NumberFormat(getLuxonLocale(locale), {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(value);
}

// ───────── 1 · Capa (NEWS-28) ─────────

export interface CoverVM {
  store: StoreView;
}

export function coverVM(facts: EstreiaFacts): CoverVM {
  return { store: storeOf(facts) };
}

// ───────── 2 · No celular (NEWS-29) ─────────

export interface PhoneVM {
  store: StoreView;
  /** Card "Faturamento pelo app", read by the finger. */
  revenue: View<{
    /** Sample series: the card shows "Exemplo". */
    sample: boolean;
    currency: string;
    total: number;
    /** `null` on the sample. */
    delta: DeltaInput | null;
    days: Day[];
    /** Where the finger rests: the top campaign, then the second. */
    peakDay: number;
    secondDay: number;
  }>;
  sales: View<{ text: string; delta: DeltaInput | null }>;
  averageTicket: View<{ text: string; delta: DeltaInput | null }>;
  todaySoFar: View<string>;
  /** Name of the next scheduled campaign, or "Nenhuma agendada". */
  nextCampaign: View<I18nText>;
}

export function phoneVM(facts: EstreiaFacts, locale: Locales): PhoneVM {
  const { currency, home } = facts;
  const series = revenueDays(facts);
  const top = topCampaigns(facts);

  const revenue = (): PhoneVM['revenue'] => {
    if (series.state === 'loading' || currency === undefined) return LOADING;
    const { sample, days } = series.value;
    if (sample) {
      return ready({
        sample,
        currency,
        total: SAMPLE_DAILY_REVENUE.reduce((sum, value) => sum + value, 0),
        delta: null,
        days,
        peakDay: SAMPLE_PEAK_DAY,
        secondDay: SAMPLE_SECOND_DAY,
      });
    }
    const campaignDays =
      top.state === 'ready'
        ? top.value.map((c) => dayIndexOf(days, c)).filter((i) => i >= 0)
        : [];
    const [peakDay, secondDay] = [...campaignDays, ...topDays(days, 2)];
    const value = home.state === 'ready' ? home.value : null;
    return ready({
      sample,
      currency,
      total: value?.current.totalRevenue ?? 0,
      delta: value
        ? {
            current: value.current.totalRevenue,
            previous: value.previous?.totalRevenue ?? null,
          }
        : null,
      days,
      peakDay: peakDay ?? 0,
      secondDay: secondDay ?? peakDay ?? 0,
    });
  };

  const tiles = (): Pick<PhoneVM, 'sales' | 'averageTicket'> => {
    if (home.state === 'loading' || currency === undefined) {
      return { sales: LOADING, averageTicket: LOADING };
    }
    if (home.state === 'missing') {
      return {
        sales: ready({ text: NO_VALUE, delta: null }),
        averageTicket: ready({ text: NO_VALUE, delta: null }),
      };
    }
    const { current, previous } = home.value;
    const ticket = ticketOf(current.totalRevenue, current.totalPaidOrders);
    return {
      sales: ready({
        text: formatCount(current.totalPaidOrders, locale),
        delta: {
          current: current.totalPaidOrders,
          previous: previous?.totalPaidOrders ?? null,
        },
      }),
      averageTicket: ready(
        ticket === null
          ? { text: NO_VALUE, delta: null }
          : {
              text: formatMoney(ticket, currency, locale) ?? NO_VALUE,
              delta: {
                current: ticket,
                previous: previous
                  ? ticketOf(previous.totalRevenue, previous.totalPaidOrders)
                  : null,
              },
            }
      ),
    };
  };

  const today = facts.todaySoFar;
  const next = facts.nextCampaign;

  return {
    store: storeOf(facts),
    revenue: revenue(),
    ...tiles(),
    todaySoFar:
      today.state === 'loading' || currency === undefined
        ? LOADING
        : ready(
            today.state === 'ready'
              ? (formatMoney(today.value.totalRevenue, currency, locale) ??
                  NO_VALUE)
              : NO_VALUE
          ),
    nextCampaign:
      next.state === 'loading'
        ? LOADING
        : ready(
            next.state === 'ready' && next.value
              ? text(next.value.name)
              : (LL) => LL.whatsNew.editions.estreia.phone.noNextCampaign()
          ),
  };
}

// ───────── 3 · CSV (NEWS-30) ─────────

export interface CsvVM {
  /** Fixed sample rows of the module: always "Exemplo". */
  sample: true;
  mappings: Array<{ column: I18nText; field: I18nText }>;
  rows: Array<{ name: I18nText; when: I18nText }>;
  scheduled: number;
  drafts: number;
}

export function csvVM(): CsvVM {
  return {
    sample: true,
    mappings: [
      {
        column: (LL) => LL.whatsNew.editions.estreia.csv.columns.title(),
        field: (LL) => LL.whatsNew.editions.estreia.csv.fields.title(),
      },
      {
        column: (LL) => LL.whatsNew.editions.estreia.csv.columns.message(),
        field: (LL) => LL.whatsNew.editions.estreia.csv.fields.message(),
      },
      {
        column: (LL) => LL.whatsNew.editions.estreia.csv.columns.date(),
        field: (LL) => LL.whatsNew.editions.estreia.csv.fields.date(),
      },
      {
        column: (LL) => LL.whatsNew.editions.estreia.csv.columns.link(),
        field: (LL) => LL.whatsNew.editions.estreia.csv.fields.link(),
      },
    ],
    rows: [
      {
        name: (LL) => LL.whatsNew.editions.estreia.csv.rows.first.name(),
        when: (LL) => LL.whatsNew.editions.estreia.csv.rows.first.when(),
      },
      {
        name: (LL) => LL.whatsNew.editions.estreia.csv.rows.second.name(),
        when: (LL) => LL.whatsNew.editions.estreia.csv.rows.second.when(),
      },
      {
        name: (LL) => LL.whatsNew.editions.estreia.csv.rows.third.name(),
        when: (LL) => LL.whatsNew.editions.estreia.csv.rows.third.when(),
      },
      {
        name: (LL) => LL.whatsNew.editions.estreia.csv.rows.fourth.name(),
        when: (LL) => LL.whatsNew.editions.estreia.csv.rows.fourth.when(),
      },
    ],
    scheduled: CSV_SCHEDULED,
    drafts: CSV_DRAFTS,
  };
}

// ───────── 4 · Korza Studio (NEWS-31) ─────────

export interface StudioVM {
  store: StoreView;
  /** What gets typed: the latest sent campaign, or the module's sample (no badge). */
  notification: View<{ title: I18nText; message: I18nText }>;
}

export function studioVM(facts: EstreiaFacts): StudioVM {
  const latest = facts.latestSentCampaign;
  return {
    store: storeOf(facts),
    notification:
      latest.state === 'loading'
        ? LOADING
        : ready(
            latest.state === 'ready' && latest.value
              ? {
                  title: text(latest.value.notificationTitle),
                  message: text(latest.value.notificationBody),
                }
              : {
                  title: (LL) =>
                    LL.whatsNew.editions.estreia.studio.sampleTitle(),
                  message: (LL) =>
                    LL.whatsNew.editions.estreia.studio.sampleMessage(),
                }
          ),
  };
}

// ───────── 5 · Início (NEWS-32) ─────────

export interface HomeVM {
  store: StoreView;
  chart: View<{
    sample: boolean;
    currency: string;
    total: number;
    /** `null` on the sample. */
    delta: DeltaInput | null;
    series: number[];
    /** 4 labels: 3 dates and "hoje, até agora". */
    axis: [I18nText, I18nText, I18nText, I18nText];
    /** The 2 top campaigns of the period (none without campaigns). */
    markers: Array<{ index: number; name: string }>;
    /** Label of the top campaign: "R$ x · n pedidos". */
    label: { index: number; name: string; result: I18nText } | null;
  }>;
  /** Cites the top campaign, or the alternative without one. */
  lead: View<I18nText>;
}

export function homeVM(facts: EstreiaFacts, locale: Locales): HomeVM {
  const { currency } = facts;
  const series = revenueDays(facts);
  const top = topCampaigns(facts);
  const first = topCampaign(facts);

  const chart = (): HomeVM['chart'] => {
    if (
      series.state === 'loading' ||
      top.state === 'loading' ||
      currency === undefined
    ) {
      return LOADING;
    }
    const { sample, days } = series.value;
    const last = days.length - 1;
    const axisAt = (k: number): I18nText => {
      const day = days[Math.round((k * last) / 3)];
      return text(day ? formatDay(day.date, locale) : '');
    };
    const marked =
      sample || top.state !== 'ready'
        ? []
        : top.value
            .slice(0, 2)
            .map((campaign) => ({
              campaign,
              index: dayIndexOf(days, campaign),
            }))
            .filter(({ index }) => index >= 0);
    const home = facts.home.state === 'ready' ? facts.home.value : null;
    const labelled = marked[0];
    return ready({
      sample,
      currency,
      total: sample
        ? SAMPLE_DAILY_REVENUE.reduce((sum, value) => sum + value, 0)
        : (home?.current.totalRevenue ?? 0),
      delta:
        sample || !home
          ? null
          : {
              current: home.current.totalRevenue,
              previous: home.previous?.totalRevenue ?? null,
            },
      series: days.map((day) => day.revenue),
      axis: [
        axisAt(0),
        axisAt(1),
        axisAt(2),
        (LL) => LL.whatsNew.editions.estreia.home.axisToday(),
      ],
      markers: marked.map(({ campaign, index }) => ({
        index,
        name: campaign.name,
      })),
      label: labelled
        ? {
            index: labelled.index,
            name: labelled.campaign.name,
            result: (LL) =>
              LL.whatsNew.editions.estreia.home.campaignResult({
                revenue:
                  formatMoney(
                    labelled.campaign.analytics.revenue,
                    currency,
                    locale
                  ) ?? NO_VALUE,
                orders: labelled.campaign.analytics.paidOrderCount,
              }),
          }
        : null,
    });
  };

  const lead = (): HomeVM['lead'] => {
    if (first.state === 'loading' || currency === undefined) return LOADING;
    const campaign = first.value;
    if (!campaign)
      return ready((LL) => LL.whatsNew.editions.estreia.home.leadNoCampaign());
    return ready((LL) =>
      LL.whatsNew.editions.estreia.home.lead({
        campaign: campaign.name,
        revenue:
          formatMoney(campaign.analytics.revenue, currency, locale) ?? NO_VALUE,
        orders: campaign.analytics.paidOrderCount,
      })
    );
  };

  return { store: storeOf(facts), chart: chart(), lead: lead() };
}

/** The Início lead's longest version, whose room the slide holds (FX8c). */
export function homeLeadLongest(locale: Locales): I18nText {
  return (LL) =>
    LL.whatsNew.editions.estreia.home.lead({
      campaign: LONGEST_CAMPAIGN_NAME,
      revenue: formatMoney(LONGEST_REVENUE, 'BRL', locale) ?? NO_VALUE,
      orders: LONGEST_ORDERS,
    });
}

// ───────── 6 · Analytics (NEWS-33) ─────────

export type AnalyticsTabId = 'overview' | 'sales' | 'push' | 'users' | 'orders';

export interface KpiView {
  label: I18nText;
  /** "—" without a value. */
  value: View<string>;
  /** `null` without a value. */
  delta: DeltaInput | null;
}

export interface AnalyticsTabView {
  id: AnalyticsTabId;
  label: I18nText;
  kpis: [KpiView, KpiView, KpiView];
  /** 7 pairs previous × current (0–1 of the tallest), the last one today. */
  bars: View<{ sample: boolean; pairs: DeltaInput[] }>;
}

export interface AnalyticsVM {
  tabs: AnalyticsTabView[];
}

/** One number of a tab from a compared fact: value text and its variation. */
function kpi<T>(
  label: I18nText,
  fact: Fact<{ current: T; previous: T | null }>,
  valueOf: (data: T) => number | null,
  format: (value: number) => string | null
): KpiView {
  if (fact.state === 'loading') return { label, value: LOADING, delta: null };
  const current = fact.state === 'ready' ? valueOf(fact.value.current) : null;
  if (current === null) return { label, value: ready(NO_VALUE), delta: null };
  const shown = format(current);
  // A money value waits for the store currency.
  if (shown === null) return { label, value: LOADING, delta: null };
  const previous =
    fact.state === 'ready' && fact.value.previous
      ? valueOf(fact.value.previous)
      : null;
  return { label, value: ready(shown), delta: { current, previous } };
}

/** The last 14 days as 7 pairs (the 7 before × the last 7), or the sample. */
function barsOf(
  fact: Fact<number[]>
): View<{ sample: boolean; pairs: DeltaInput[] }> {
  if (fact.state === 'loading') return LOADING;
  const last = fact.state === 'ready' ? fact.value.slice(-14) : [];
  const max = Math.max(0, ...last);
  if (last.length < 14 || max <= 0) {
    const sampleMax = Math.max(
      ...SAMPLE_BAR_PAIRS.flatMap((p) => [p.previous, p.current])
    );
    return ready({
      sample: true,
      pairs: SAMPLE_BAR_PAIRS.map((p) => ({
        previous: p.previous / sampleMax,
        current: p.current / sampleMax,
      })),
    });
  }
  return ready({
    sample: false,
    pairs: Array.from({ length: 7 }, (_, i) => ({
      previous: last[i] / max,
      current: last[i + 7] / max,
    })),
  });
}

function mapFact<T, U>(fact: Fact<T>, map: (value: T) => U): Fact<U> {
  return fact.state === 'ready'
    ? { state: 'ready', value: map(fact.value) }
    : fact;
}

export function analyticsVM(facts: EstreiaFacts, locale: Locales): AnalyticsVM {
  const { currency, sales, users, campaignPerformance, sessions, home } = facts;
  const money = (value: number) => formatMoney(value, currency, locale);
  const count = (value: number) => formatCount(value, locale);
  const share = (value: number) => percent(value, locale);

  const homePoints = mapFact(home, (h) => h.current.revenueChart.points);
  const sessionsTotal = mapFact(sessions, ({ current, previous }) => ({
    current: current.points.reduce((sum, p) => sum + p.sessionsCount, 0),
    previous: previous
      ? previous.points.reduce((sum, p) => sum + p.sessionsCount, 0)
      : null,
  }));
  const ticket = (s: TenantSalesMetricsResponseDto) =>
    s.totalPaidOrders > 0 ? s.aov : null;

  return {
    tabs: [
      {
        id: 'overview',
        label: (LL) => LL.whatsNew.editions.estreia.analytics.tabs.overview(),
        kpis: [
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.appRevenue(),
            sales,
            (s) => s.totalRevenue,
            money
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.sales(),
            sales,
            (s) => s.totalPaidOrders,
            count
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.downloads(),
            users,
            (u) => u.downloads,
            count
          ),
        ],
        bars: barsOf(
          mapFact(homePoints, (points) => points.map((p) => p.grossRevenueUsd))
        ),
      },
      {
        id: 'sales',
        label: (LL) => LL.whatsNew.editions.estreia.analytics.tabs.sales(),
        kpis: [
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.averageTicket(),
            sales,
            ticket,
            money
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.appShare(),
            sales,
            (s) => appShare(s.totalRevenue, s.web.revenue),
            share
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.revenue(),
            sales,
            (s) => s.totalRevenue,
            money
          ),
        ],
        bars: barsOf(
          mapFact(homePoints, (points) =>
            points.map((p) => ticketOf(p.grossRevenueUsd, p.count) ?? 0)
          )
        ),
      },
      {
        id: 'push',
        label: (LL) => LL.whatsNew.editions.estreia.analytics.tabs.push(),
        kpis: [
          kpi(
            (LL) =>
              LL.whatsNew.editions.estreia.analytics.kpis.averageCampaignRevenue(),
            campaignPerformance,
            (c) => (c.sentCampaignsCount > 0 ? c.averageRevenue : null),
            money
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.clicks(),
            campaignPerformance,
            (c) => c.totalClicks,
            count
          ),
          kpi(
            (LL) =>
              LL.whatsNew.editions.estreia.analytics.kpis.attributedOrders(),
            campaignPerformance,
            (c) => c.totalOrderPaidCount,
            count
          ),
        ],
        bars: barsOf(
          mapFact(facts.campaignOrdersChart, (chart) =>
            chart.points.map((p) => p.revenue)
          )
        ),
      },
      {
        id: 'users',
        label: (LL) => LL.whatsNew.editions.estreia.analytics.tabs.users(),
        kpis: [
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.activeUsers(),
            users,
            (u) => u.activeUsers,
            count
          ),
          kpi(
            (LL) =>
              LL.whatsNew.editions.estreia.analytics.kpis.downloadedAndBought(),
            users,
            (u) => u.convertedDownloads.count,
            count
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.sessions(),
            sessionsTotal,
            (total) => total,
            count
          ),
        ],
        bars: barsOf(
          mapFact(sessions, ({ current }) =>
            current.points.map((p) => p.sessionsCount)
          )
        ),
      },
      {
        id: 'orders',
        label: (LL) => LL.whatsNew.editions.estreia.analytics.tabs.orders(),
        kpis: [
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.orders(),
            sales,
            (s) => s.totalPaidOrders,
            count
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.averageTicket(),
            sales,
            ticket,
            money
          ),
          kpi(
            (LL) => LL.whatsNew.editions.estreia.analytics.kpis.repurchases(),
            sales,
            repurchaseCount,
            count
          ),
        ],
        bars: barsOf(
          mapFact(homePoints, (points) => points.map((p) => p.count))
        ),
      },
    ],
  };
}

// ───────── 7 · Seu app (NEWS-34) ─────────

export type AppRowId = 'coupon' | 'welcome' | 'site';

export interface AppRowView {
  id: AppRowId;
  title: I18nText;
  state: View<{
    /** Where the switch ends up (the real state). */
    on: boolean;
    /** The real setting, or "Ainda não configurado". */
    detail: I18nText;
    /** Coupon banner on: "13,4:1 · Legível" / "· Baixo". */
    contrast: { text: I18nText; legible: boolean } | null;
  }>;
  /**
   * A detail whose length is known before the row loads, held loading and
   * loaded so the row never changes height (the site's, FX8c); else null.
   */
  reserve: I18nText | null;
}

export interface AppVM {
  store: StoreView;
  rows: [AppRowView, AppRowView, AppRowView];
}

const notConfigured = {
  on: false,
  detail: (LL: TranslationFunctions) =>
    LL.whatsNew.editions.estreia.app.notConfigured(),
  contrast: null,
};

function rowState<T>(
  fact: Fact<T | null>,
  configured: (
    value: T
  ) => Exclude<AppRowView['state'], { state: 'loading' }>['value']
): AppRowView['state'] {
  if (fact.state === 'loading') return LOADING;
  if (fact.state === 'missing' || fact.value === null) {
    return ready(notConfigured);
  }
  return ready(configured(fact.value));
}

export function appVM(facts: EstreiaFacts, locale: Locales): AppVM {
  const domain = facts.store.domain;
  const siteDetail: I18nText = (LL) =>
    domain
      ? LL.whatsNew.editions.estreia.app.site.domain({ domain })
      : LL.whatsNew.editions.estreia.app.site.noDomain();
  return {
    store: storeOf(facts),
    rows: [
      {
        id: 'coupon',
        reserve: null,
        title: (LL) => LL.whatsNew.editions.estreia.app.coupon.title(),
        state: rowState(facts.couponBanner, (coupon) => {
          const bg = normalizeHex(coupon.backgroundColorHex);
          const fg = normalizeHex(coupon.foregroundColorHex);
          const ratio = bg && fg ? contrastRatio(bg, fg) : null;
          return {
            on: coupon.isActive,
            detail: (LL) =>
              coupon.position === 'bottom'
                ? LL.whatsNew.editions.estreia.app.coupon.bottom()
                : LL.whatsNew.editions.estreia.app.coupon.top(),
            contrast:
              coupon.isActive && ratio !== null
                ? {
                    legible: meetsAA(ratio),
                    text: (LL) => {
                      const value = formatContrastRatio(ratio, locale);
                      return meetsAA(ratio)
                        ? LL.whatsNew.editions.estreia.app.coupon.contrastLegible(
                            {
                              ratio: value,
                            }
                          )
                        : LL.whatsNew.editions.estreia.app.coupon.contrastLow({
                            ratio: value,
                          });
                    },
                  }
                : null,
          };
        }),
      },
      {
        id: 'welcome',
        reserve: null,
        title: (LL) => LL.whatsNew.editions.estreia.app.welcome.title(),
        state: rowState(facts.welcomeNotification, (welcome) => ({
          on: welcome.isActive,
          detail: (LL) =>
            LL.whatsNew.editions.estreia.app.welcome.delay({
              minutes: welcome.delayMinutes,
            }),
          contrast: null,
        })),
      },
      {
        id: 'site',
        title: (LL) => LL.whatsNew.editions.estreia.app.site.title(),
        state: rowState(facts.websiteBanner, (banner) => ({
          on: banner.enabled,
          detail: siteDetail,
          contrast: null,
        })),
        reserve: siteDetail,
      },
    ],
  };
}

// ───────── 8 · E mais (NEWS-35) ─────────

export type MoreTileId =
  | 'team'
  | 'testPush'
  | 'duplicate'
  | 'orders'
  | 'audiences'
  | 'signup';

export interface MoreTileView {
  id: MoreTileId;
  title: I18nText;
  description: I18nText;
  /** Where to find it; "Duplicar" names the top campaign. */
  where: View<I18nText>;
}

export interface MoreVM {
  tiles: MoreTileView[];
}

export function moreVM(facts: EstreiaFacts): MoreVM {
  const top = topCampaign(facts);
  return {
    tiles: [
      {
        id: 'team',
        title: (LL) => LL.whatsNew.editions.estreia.more.tiles.team.title(),
        description: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.team.description(),
        where: ready((LL) =>
          LL.whatsNew.editions.estreia.more.tiles.team.where()
        ),
      },
      {
        id: 'testPush',
        title: (LL) => LL.whatsNew.editions.estreia.more.tiles.testPush.title(),
        description: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.testPush.description(),
        where: ready((LL) =>
          LL.whatsNew.editions.estreia.more.tiles.testPush.where()
        ),
      },
      {
        id: 'duplicate',
        title: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.duplicate.title(),
        description: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.duplicate.description(),
        where:
          top.state === 'loading'
            ? LOADING
            : ready((LL) => {
                const campaign = top.value;
                return campaign
                  ? LL.whatsNew.editions.estreia.more.tiles.duplicate.copyOf({
                      campaign: campaign.name,
                    })
                  : LL.whatsNew.editions.estreia.more.tiles.duplicate.anyCampaign();
              }),
      },
      {
        id: 'orders',
        title: (LL) => LL.whatsNew.editions.estreia.more.tiles.orders.title(),
        description: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.orders.description(),
        where: ready((LL) =>
          LL.whatsNew.editions.estreia.more.tiles.orders.where()
        ),
      },
      {
        id: 'audiences',
        title: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.audiences.title(),
        description: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.audiences.description(),
        where: ready((LL) =>
          LL.whatsNew.editions.estreia.more.tiles.audiences.where()
        ),
      },
      {
        id: 'signup',
        title: (LL) => LL.whatsNew.editions.estreia.more.tiles.signup.title(),
        description: (LL) =>
          LL.whatsNew.editions.estreia.more.tiles.signup.description(),
        where: ready((LL) =>
          LL.whatsNew.editions.estreia.more.tiles.signup.where()
        ),
      },
    ],
  };
}

// ───────── 9 · Encerramento (NEWS-12, NEWS-36) ─────────

export interface OutroVM {
  store: StoreView;
  /** The top campaign's pin and the next scheduled one, or the alternative. */
  lead: View<I18nText>;
}

export function outroVM(facts: EstreiaFacts): OutroVM {
  const top = topCampaign(facts);
  const next = facts.nextCampaign;
  const lead = (): OutroVM['lead'] => {
    if (top.state === 'loading' || next.state === 'loading') return LOADING;
    const campaign = top.value;
    if (!campaign)
      return ready((LL) => LL.whatsNew.editions.estreia.outro.leadNoCampaign());
    const scheduled = next.state === 'ready' ? next.value : null;
    return ready((LL) =>
      [
        LL.whatsNew.editions.estreia.outro.leadPin({ campaign: campaign.name }),
        ...(scheduled
          ? [
              LL.whatsNew.editions.estreia.outro.leadNext({
                campaign: scheduled.name,
              }),
            ]
          : []),
      ].join(' ')
    );
  };
  return { store: storeOf(facts), lead: lead() };
}

/** The outro lead's longest version (pin and next campaign), held (FX8c). */
export const outroLeadLongest: I18nText = (LL) =>
  [
    LL.whatsNew.editions.estreia.outro.leadPin({
      campaign: LONGEST_CAMPAIGN_NAME,
    }),
    LL.whatsNew.editions.estreia.outro.leadNext({
      campaign: LONGEST_CAMPAIGN_NAME,
    }),
  ].join(' ');
